<script lang="ts">
    import { page as currentPage } from '$app/stores';
    import { cn } from '$lib/utils/cn';

    import { onMount } from 'svelte';

    import {
        DEFAULT_SCROLL_THRESHOLD,
        SCROLL_THROTTLE_MS,
        TRANSLUCENT_OPACITY_SUFFIX,
    } from './constants';
    import Block from '../Blocks/Block.svelte';

    import type { Component } from '$lib/types/Components/SharedTypes';

    export let bgColor = 'transparent';
    export let fixed = false;
    export let translucent = false;
    export let components: Component[] = [];
    export let align: 'left' | 'center' | 'right' | 'left-right' = 'left';
    export let onLogoClick: (event: MouseEvent | TouchEvent) => void;
    export let scrollThreshold = DEFAULT_SCROLL_THRESHOLD;
    export let hidden = false;
    export let mockPageData: any = null; // Optional page data for Storybook/testing

    // Use pageData if provided (for Storybook), otherwise fall back to $currentPage.data
    $: effectivePageData = mockPageData || $currentPage.data;

    let isScrolled = false;
    let isValidConfig = true;
    let leftSlotComponents: Component[] = [];
    let rightSlotComponents: Component[] = [];

    // Throttle scroll events for better performance
    let scrollTimeout: number;
    const handleScroll = () => {
        if (scrollTimeout) {
            clearTimeout(scrollTimeout);
        }
        scrollTimeout = window.setTimeout(() => {
            isScrolled = window.scrollY > scrollThreshold;
        }, SCROLL_THROTTLE_MS);
    };

    onMount(() => {
        if (fixed) {
            window.addEventListener('scroll', handleScroll, { passive: true });
            return () => {
                window.removeEventListener('scroll', handleScroll);

                if (scrollTimeout) {
                    clearTimeout(scrollTimeout);
                }
            };
        }
    });

    const alignMap = {
        left: 'fun-justify-start',
        center: 'fun-justify-center',
        right: 'fun-justify-end',
        'left-right': 'fun-justify-between',
    };

    const validateAndSetupComponents = () => {
        if (fixed && bgColor === 'transparent') {
            bgColor = effectivePageData?.theme?.backgroundColor || '#ffffff';
        }

        const gridRowComponent = components.find((component) => component.name === 'headerGridRow');

        if (!gridRowComponent) {
            console.warn(
                'Custom header: No headerGridRow component found, header may not render correctly',
            );
            isValidConfig = false;
            return;
        }

        if (gridRowComponent.components.length < 1) {
            console.warn('Custom header: headerGridRow must have at least 1 component');
            isValidConfig = false;
            return;
        }

        const leftSlotComponent = gridRowComponent.components[0];
        const rightSlotComponent = gridRowComponent.components[1];

        leftSlotComponents = leftSlotComponent?.components || [];
        rightSlotComponents = rightSlotComponent?.components || [];

        isValidConfig = true;
    };

    validateAndSetupComponents();
</script>

{#if !hidden}
    <div
        class={cn(
            'fun-border-box fun-w-full fun-px-4 fun-py-2 fun-transition-all fun-duration-300',
            {
                'fun-sticky fun-top-0 fun-z-50': fixed,
            },
        )}
        style={`background-color: ${bgColor}${
            fixed && translucent ? TRANSLUCENT_OPACITY_SUFFIX : ''
        };
        ${
            fixed && translucent
                ? 'backdrop-filter: url(filters.svg#filter) blur(20px) saturate(1.8);'
                : ''
        }
     box-shadow: ${
         fixed && isScrolled && !translucent
             ? '0px 0px 0px 1px rgba(17, 24, 39, 0.08), 0px 2px 2px 0px rgba(17, 24, 39, 0.01), 0px 4px 4px 0px rgba(17, 24, 39, 0.01), 0px 8px 8px 0px rgba(17, 24, 39, 0.01), 0px 16px 16px 0px rgba(17, 24, 39, 0.01), 0px 32px 32px 0px rgba(17, 24, 39, 0.01);'
             : 'none'
     }`}
        role="banner"
        aria-label="Site header"
        data-is-fixed={fixed}
    >
        {#if effectivePageData && isValidConfig}
            <div class={cn('fun-flex fun-items-center', alignMap[align])}>
                <div class="fun-flex fun-items-start">
                    {#each leftSlotComponents as component, index (component._id)}
                        <Block
                            id={component._id}
                            language={effectivePageData.language}
                            campaignId={effectivePageData.campaignId}
                            campaignVersionId={effectivePageData.campaignVersionId}
                            companyId={effectivePageData.companyId}
                            cookiesEnabled={effectivePageData.cookiesEnabled}
                            componentType={component.componentType}
                            childComponents={component.components}
                            content={component.content}
                            theme={effectivePageData.theme}
                            nextPage={effectivePageData.nextPage}
                            currentPage={effectivePageData.pageId}
                            resultMapping={effectivePageData.resultMapping}
                            fields={effectivePageData.fields}
                            firstResultPage={effectivePageData.firstResultPage}
                            trackingProperties={effectivePageData.trackingProperties}
                            onClick={onLogoClick}
                            {index}
                        />
                    {/each}
                </div>
                {#if align === 'left-right' && rightSlotComponents.length > 0}
                    <div class="fun-flex fun-items-end">
                        {#each rightSlotComponents as component, index (component._id)}
                            <Block
                                id={component._id}
                                language={effectivePageData.language}
                                campaignId={effectivePageData.campaignId}
                                campaignVersionId={effectivePageData.campaignVersionId}
                                companyId={effectivePageData.companyId}
                                cookiesEnabled={effectivePageData.cookiesEnabled}
                                componentType={component.componentType}
                                childComponents={component.components}
                                content={component.content}
                                theme={effectivePageData.theme}
                                nextPage={effectivePageData.nextPage}
                                currentPage={effectivePageData.pageId}
                                resultMapping={effectivePageData.resultMapping}
                                fields={effectivePageData.fields}
                                firstResultPage={effectivePageData.firstResultPage}
                                trackingProperties={effectivePageData.trackingProperties}
                                onClick={onLogoClick}
                                {index}
                            />
                        {/each}
                    </div>
                {/if}
            </div>
        {/if}
    </div>
{/if}
